import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useTenant } from '@/contexts/TenantContext';
import { createTenant, CreateTenantData } from '@/services/tenantService';
import { Loader2, Building2 } from 'lucide-react';

interface CreateTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateTenantDialog({ open, onOpenChange }: CreateTenantDialogProps) {
  const { toast } = useToast();
  const { addTenant } = useTenant();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreateTenantData>({
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof CreateTenantData, string>>>({});

  const handleChange = (field: keyof CreateTenantData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CreateTenantData, string>> = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }

    if (!formData.contact_name.trim()) {
      newErrors.contact_name = 'Contact name is required';
    }

    if (!formData.contact_email.trim()) {
      newErrors.contact_email = 'Contact email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contact_email)) {
        newErrors.contact_email = 'Invalid email format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await createTenant(formData);

      if (result.success && result.tenant) {
        // Add the new tenant to the dropdown list immediately
        addTenant(result.tenant);

        // Show success message
        toast({
          title: '✓ Tenant Created Successfully',
          description: `${result.tenant.company_name} is ready! You can now switch to it from the tenant dropdown.`,
          duration: 6000,
        });

        // Reset form and close dialog
        setFormData({
          company_name: '',
          contact_name: '',
          contact_email: '',
          contact_phone: '',
        });
        setErrors({});
        onOpenChange(false);
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to create tenant',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error creating tenant:', error);
      toast({
        title: 'Error',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      // Reset form
      setFormData({
        company_name: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
      });
      setErrors({});
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center space-x-2">
            <Building2 className="h-5 w-5 text-primary" />
            <DialogTitle>Create New Tenant</DialogTitle>
          </div>
          <DialogDescription>
            Create a new tenant organization. This will set up a new isolated environment with default settings.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="company_name">
              Company Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="company_name"
              value={formData.company_name}
              onChange={handleChange('company_name')}
              placeholder="Acme Solar Solutions"
              disabled={isSubmitting}
              className={errors.company_name ? 'border-red-500' : ''}
            />
            {errors.company_name && (
              <p className="text-sm text-red-500">{errors.company_name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_name">
              Contact Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="contact_name"
              value={formData.contact_name}
              onChange={handleChange('contact_name')}
              placeholder="John Smith"
              disabled={isSubmitting}
              className={errors.contact_name ? 'border-red-500' : ''}
            />
            {errors.contact_name && (
              <p className="text-sm text-red-500">{errors.contact_name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_email">
              Contact Email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="contact_email"
              type="email"
              value={formData.contact_email}
              onChange={handleChange('contact_email')}
              placeholder="john@acmesolar.com"
              disabled={isSubmitting}
              className={errors.contact_email ? 'border-red-500' : ''}
            />
            {errors.contact_email && (
              <p className="text-sm text-red-500">{errors.contact_email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_phone">Contact Phone</Label>
            <Input
              id="contact_phone"
              type="tel"
              value={formData.contact_phone}
              onChange={handleChange('contact_phone')}
              placeholder="(555) 123-4567"
              disabled={isSubmitting}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Tenant'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
